import { useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type PaginationState,
} from '@tanstack/react-table';
import { CaretLeft, CaretRight, Funnel } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { DataTablePagination, ErrorAlert, PageHeader } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { useIsMobile } from '@/hooks/use-media-query';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { DepartmentFilter } from '@/modules/settings/organization/departments';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { LeaveCalendarFilterDialog } from '../../components/leave-calendar-filter-dialog/leave-calendar-filter-dialog';
import { LeaveCalendarGrid } from '../../components/leave-calendar-grid/leave-calendar-grid';
import { LeaveCalendarMobile } from '../../components/leave-calendar-mobile/leave-calendar-mobile';
import { useGetLeaveCalendarData } from '../../hooks/use-leave-calendar';
import { LeaveCalendarEmployeeGroup } from '../../types/leave-calendar.types';
import {
  addDays,
  addMonthsKeepingDay,
  formatDayLabel,
  monthDaysOf,
  monthStartOf,
  weekDaysFrom,
  weekStartOf,
} from '../../utils/leave-calendar-week';

type CalendarView = 'week' | 'month';

const FIRST_PAGE: PaginationState = { pageIndex: 0, pageSize: 15 };
const NO_EMPLOYEES: LeaveCalendarEmployeeGroup[] = [];

/** Today ("YYYY-MM-DD") in the organization's time zone, never the device's. */
const orgToday = () => {
  const { year, month, day } = orgWallClock(new Date());
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/** The days a view shows around `day`: its Monday-to-Sunday week, or its whole month. */
const daysOf = (view: CalendarView, day: string) =>
  view === 'week' ? weekDaysFrom(weekStartOf(day)) : monthDaysOf(day);

export const LeaveCalendarPage = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const today = useMemo(orgToday, []);
  const [view, setView] = useState<CalendarView>('week');
  // Any day of the period shown; the view turns it into its week or month. On a phone it is also the
  // day whose leave is listed, so it survives switching views or resizing.
  const [anchor, setAnchor] = useState(today);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const initialPageSize = useInitialPageSize(FIRST_PAGE.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    ...FIRST_PAGE,
    pageSize: initialPageSize,
  });

  const days = useMemo(() => daysOf(view, anchor), [view, anchor]);
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  // The department scope (the Department permissions, as the Leave Manager) always applies, so a head
  // of department sees only their departments' leave; the picker only narrows within it.
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const { data, isLoading, error } = useGetLeaveCalendarData({
    startDate: firstDay,
    endDate: lastDay,
    status: 'Approved',
    departmentId: getDepartmentIdCondition(departmentId),
  });

  const toFirstPage = () => setPagination((current) => ({ ...current, pageIndex: 0 }));
  const goTo = (day: string) => {
    setAnchor(day);
    toFirstPage();
  };
  // Moves by whole periods from the anchor, keeping its weekday (week) or day of the month (month),
  // and lands on today when the new period holds it.
  const step = (direction: 1 | -1) => {
    const next =
      view === 'week' ? addDays(anchor, 7 * direction) : addMonthsKeepingDay(anchor, direction);
    goTo(daysOf(view, next).includes(today) ? today : next);
  };

  const periodLabel =
    view === 'month'
      ? formatDayLabel(firstDay, i18n.language, { month: 'long', year: 'numeric' })
      : `${formatDayLabel(firstDay, i18n.language, { day: 'numeric', month: 'short' })} - ${formatDayLabel(lastDay, i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  // Every employee on leave in the period is loaded at once, so the page searches and pages them itself.
  const employees = data?.employees ?? NO_EMPLOYEES;
  const matching = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return keyword
      ? employees.filter((employee) => employee.DisplayName.toLowerCase().includes(keyword))
      : employees;
  }, [employees, search]);

  const table = useReactTable({
    data: matching,
    columns: [],
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
  });
  const pageEmployees = table.getRowModel().rows.map((row) => row.original);

  // A group's ItemId is the employee's IAM user id. The phone view looks up only the employee it opens.
  const { byUserId } = useEmployeesByUserIds(
    isMobile ? [] : pageEmployees.map((employee) => employee.ItemId)
  );
  const emailOf = (userId: string) => byUserId.get(userId)?.Email;

  const changeView = (next: string) => {
    setView(next as CalendarView);
    toFirstPage();
  };
  const changeDepartment = (next: string) => {
    setDepartmentId(next);
    toFirstPage();
  };
  const changeSearch = (next: string) => {
    setSearch(next);
    toFirstPage();
  };

  const viewTabs = (
    <Tabs value={view} onValueChange={changeView}>
      <TabsList>
        <TabsTrigger value="week">{t('LEAVE_CALENDAR_WEEK')}</TabsTrigger>
        <TabsTrigger value="month">{t('LEAVE_CALENDAR_MONTH')}</TabsTrigger>
      </TabsList>
    </Tabs>
  );
  const todayButton = (
    <Button variant="outline" onClick={() => goTo(today)}>
      {t('TODAY')}
    </Button>
  );
  const stepButton = (direction: 1 | -1) => (
    <Button
      variant="outline"
      size="icon"
      className="shrink-0"
      aria-label={t(
        direction === 1
          ? view === 'week'
            ? 'LEAVE_CALENDAR_NEXT_WEEK'
            : 'LEAVE_CALENDAR_NEXT_MONTH'
          : view === 'week'
            ? 'LEAVE_CALENDAR_PREVIOUS_WEEK'
            : 'LEAVE_CALENDAR_PREVIOUS_MONTH'
      )}
      onClick={() => step(direction)}
    >
      {direction === 1 ? <CaretRight className="h-4 w-4" /> : <CaretLeft className="h-4 w-4" />}
    </Button>
  );
  const isLoadingData = isLoading || isScopeLoading;
  // The search and the picked department; a department locked by the scope is not counted.
  const activeFilters = Number(search.trim() !== '') + Number(departmentId !== '');

  const mobileToolbar = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {stepButton(-1)}
        <span className="min-w-0 flex-1 truncate text-center text-sm font-medium">
          {periodLabel}
        </span>
        {stepButton(1)}
      </div>
      <div className="flex items-center gap-2">
        {viewTabs}
        <div className="ml-auto flex items-center gap-2">
          {todayButton}
          <Button
            type="button"
            variant="outline"
            size="icon"
            // As tall as the buttons beside it, like the card tables' filter button.
            className="relative size-9 shrink-0"
            aria-label={t('FILTER')}
            onClick={() => setIsFilterOpen(true)}
          >
            <Funnel className="size-5" weight={activeFilters > 0 ? 'fill' : 'regular'} />
            {activeFilters > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
                {activeFilters}
              </span>
            )}
          </Button>
        </div>
      </div>

      <LeaveCalendarFilterDialog
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        applied={{ day: anchor, departmentId, search }}
        onApply={(filters) => {
          setAnchor(filters.day);
          setDepartmentId(filters.departmentId);
          setSearch(filters.search);
          toFirstPage();
        }}
      />
    </div>
  );

  const desktopToolbar = (
    <div className="flex flex-wrap items-center gap-2">
      {viewTabs}
      {todayButton}
      {stepButton(-1)}
      {stepButton(1)}
      <DatePicker
        className="w-44"
        aria-label={t(
          view === 'week' ? 'LEAVE_CALENDAR_GO_TO_DATE' : 'LEAVE_CALENDAR_GO_TO_MONTH_OF_DATE'
        )}
        value={view === 'week' ? firstDay : monthStartOf(firstDay)}
        onChange={(day) => day && goTo(day)}
      />
      <span className="text-sm font-medium">{periodLabel}</span>
      <DepartmentFilter
        value={departmentId}
        onChange={changeDepartment}
        selectClassName="ml-auto w-44"
      />
      <Input
        className="w-64"
        placeholder={t('SEARCH_BY_NAME')}
        aria-label={t('SEARCH_BY_NAME')}
        value={search}
        onChange={(event) => changeSearch(event.target.value)}
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Request Time Off and Request TOIL are on My Time Off only (2 Nov 2026). */}
      <PageHeader title={t('CALENDAR')} />

      {isMobile ? mobileToolbar : desktopToolbar}

      {error ? (
        <ErrorAlert isError message={error instanceof Error ? error.message : String(error)} />
      ) : isMobile ? (
        <LeaveCalendarMobile
          days={days}
          selectedDay={anchor}
          today={today}
          employees={matching}
          holidays={data?.holidays ?? []}
          isLoading={isLoadingData}
          onSelectDay={goTo}
          onStep={step}
        />
      ) : (
        <LeaveCalendarGrid
          days={days}
          today={today}
          employees={pageEmployees}
          holidays={data?.holidays ?? []}
          // Until the departments load, a scoped user's scope matches nothing; show the loader.
          isLoading={isLoadingData}
          emptyMessage={t(view === 'week' ? 'LEAVE_CALENDAR_EMPTY' : 'LEAVE_CALENDAR_EMPTY_MONTH')}
          emailOf={emailOf}
        />
      )}

      {!isMobile && matching.length > 0 && (
        <DataTablePagination
          table={table}
          showSelectedRowContent={false}
          totalCount={matching.length}
        />
      )}

    </div>
  );
};
